/**
 * AVA Design System — StatusBadge
 *
 * Badge con tonos semánticos mapeados a estados KYC:
 *   success → OK / APROBADO / face-match pass
 *   warn    → PENDING / OBSERVADO / retry
 *   info    → PROCESSING / progreso neutro
 *   danger  → INCORRECT / REJECTED / AUTO_REJECTED
 *   neutral → REPLACED / EXPIRED (de-énfasis)
 *
 * Usa los tokens --ava-{tone}-{50|200|700} de index.css.
 * El icono es opcional — si no se provee, no se renderiza ninguno
 * (el DS no fuerza emojis ni iconos por default).
 */
import type { ReactNode } from "react";

export type StatusTone = "success" | "warn" | "info" | "danger" | "neutral";

interface StatusBadgeProps {
  tone?: StatusTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

const TONE_STYLES: Record<StatusTone, { bg: string; color: string; border: string }> = {
  success: {
    bg: "var(--ava-success-50)",
    color: "var(--ava-success-700)",
    border: "var(--ava-success-200)",
  },
  warn: {
    bg: "var(--ava-warn-50)",
    color: "var(--ava-warn-700)",
    border: "var(--ava-warn-200)",
  },
  info: {
    bg: "var(--ava-info-50)",
    color: "var(--ava-info-700)",
    border: "var(--ava-info-200)",
  },
  danger: {
    bg: "var(--ava-danger-50)",
    color: "var(--ava-danger-700)",
    border: "var(--ava-danger-200)",
  },
  neutral: {
    bg: "var(--ava-neutral-100)",
    color: "var(--ava-neutral-700)",
    border: "var(--ava-neutral-200)",
  },
};

export function StatusBadge({ tone = "neutral", icon, children, className }: StatusBadgeProps) {
  const styles = TONE_STYLES[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-medium border ${className ?? ""}`}
      style={{
        backgroundColor: styles.bg,
        color: styles.color,
        borderColor: styles.border,
      }}
    >
      {icon}
      {children}
    </span>
  );
}
